import { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from './Card';
import { api, uploadPhoto, type PhotoMeta } from '../lib/api';

const ANGLES: Array<PhotoMeta['angle']> = ['front', 'side', 'back'];

export function PhotosCard({ date, isMonday }: { date: string; isMonday: boolean }) {
  const [photos, setPhotos] = useState<Record<string, PhotoMeta>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listPhotos(date);
      const map: Record<string, PhotoMeta> = {};
      for (const p of list ?? []) {
        map[p.angle] = p;
      }
      setPhotos(map);
    } catch (e) {
      setError(String(e));
    }
  }, [date]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onPick = async (angle: PhotoMeta['angle'], file: File) => {
    setBusy((b) => ({ ...b, [angle]: true }));
    setError(null);
    try {
      await uploadPhoto(file, angle, date);
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy((b) => ({ ...b, [angle]: false }));
    }
  };

  const onDelete = async (id: number, angle: string) => {
    setBusy((b) => ({ ...b, [angle]: true }));
    try {
      await api.deletePhoto(id);
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy((b) => ({ ...b, [angle]: false }));
    }
  };

  return (
    <Card
      title="Weekly photos"
      hint={isMonday ? 'Take 3 angles in identical lighting.' : 'Take or replace any time. Same lighting + posture for week-over-week comparison.'}
    >
      <div className="grid grid-cols-3 gap-2">
        {ANGLES.map((angle) => (
          <PhotoSlot
            key={angle}
            angle={angle}
            photo={photos[angle]}
            busy={!!busy[angle]}
            onPick={(f) => onPick(angle, f)}
            onDelete={(id) => onDelete(id, angle)}
          />
        ))}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </Card>
  );
}

function PhotoSlot({
  angle,
  photo,
  busy,
  onPick,
  onDelete,
}: {
  angle: PhotoMeta['angle'];
  photo?: PhotoMeta;
  busy: boolean;
  onPick: (f: File) => void;
  onDelete: (id: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const trigger = () => inputRef.current?.click();
  return (
    <div className="space-y-1.5">
      <button
        onClick={trigger}
        className="block w-full aspect-[3/4] rounded-control overflow-hidden border border-line bg-bg-sunken relative hover:border-line-strong transition-colors"
      >
        {photo ? (
          <img src={photo.url} alt={angle} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
            {busy ? '…' : '+ Add'}
          </span>
        )}
      </button>
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em]">
        <span className="text-fg-muted">{angle}</span>
        {photo && (
          <button
            onClick={() => onDelete(photo.id)}
            disabled={busy}
            className="text-fg-faint hover:text-danger transition-colors"
          >
            remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}
